import React from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@talis/auth-js";

import { AppShell } from "./components/layout/AppShell";
import { AuthGate } from "./components/layout/AuthGate";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { OrgProvider } from "./hooks/useOrg";
import { ToastProvider } from "./components/common/Toast";

import { Landing } from "./pages/Landing";
import { Overview } from "./pages/Overview";
import { DeviceFleet } from "./pages/DeviceFleet";
import { DeviceLayout } from "./pages/device/DeviceLayout";
import { OpticDetail } from "./pages/OpticDetail";
import { ResultsExplorer } from "./pages/results/ResultsExplorer";
import { OrgAlerts } from "./pages/OrgAlerts";
import { OrgSchedule } from "./pages/OrgSchedule";
import { AdminFirmware } from "./pages/AdminFirmware";
import { AdminOrgs } from "./pages/admin/AdminOrgs";
import { AdminDevices } from "./pages/admin/AdminDevices";
import { NotFound } from "./pages/NotFound";

import { Dashboard } from "./pages/device/Dashboard";
import DeviceStatus from "./pages/device/DeviceStatus";
import DeviceSchedule from "./pages/device/DeviceSchedule";
import Results from "./pages/results/Results";
import SingleResult from "./pages/results/SingleResult";
import ResultDetails from "./pages/results/ResultDetails";
import SensorSummary from "./pages/results/SensorSummary";
import Trends from "./pages/results/Trends";
import Alerts from "./pages/results/Alerts";
import AccountSettings from "./pages/account/AccountSettings";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// kraken is served on multiple domains (kraken.talis-ai.com, app.krakensense.ca)
// and authenticates via the backend OIDC flow (ait_auth_oidc). Point the auth
// library at this app's OWN origin so /api/me, /api/token/refresh and the
// /sso/* routes are all same-origin on whichever host we're served from.
const ORIGIN = window.location.origin;


export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider
            authUrl={ORIGIN}
            appId="kraken"
            appUrl={ORIGIN}
            allowedRedirects={[ORIGIN]}
            ssoStartPath="/sso/start"
          >
          <AuthGate>
          <OrgProvider>
          <ToastProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route element={<AppShell />}>
              {/* Org-level sections */}
              <Route path="/overview" element={<Overview />} />
              <Route path="/results" element={<ResultsExplorer />} />
              <Route path="/devices" element={<DeviceFleet />} />
              <Route path="/alerts" element={<OrgAlerts />} />
              <Route path="/schedule" element={<OrgSchedule />} />

              {/* Optic detail — full page (no device tabs). Kept device-scoped so
                  the existing scientific views work unchanged; the cross-fleet
                  explorer links in via the optic's device_id. */}
              <Route path="/devices/:deviceId/results/:opticId" element={<SingleResult />} />
              <Route path="/devices/:deviceId/results/:opticId/details" element={<ResultDetails />} />
              <Route path="/optics/:opticId" element={<OpticDetail />} />

              {/* Device detail — tabbed shell with nested tab bodies */}
              <Route path="/devices/:deviceId" element={<DeviceLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<Dashboard />} />
                <Route path="results" element={<Results />} />
                <Route path="summary" element={<SensorSummary />} />
                <Route path="trends" element={<Trends />} />
                <Route path="health" element={<DeviceStatus />} />
                <Route path="schedule" element={<DeviceSchedule />} />
                <Route path="alerts" element={<Alerts />} />
                {/* legacy path redirects */}
                <Route path="dashboard" element={<Navigate to="../overview" replace />} />
                <Route path="status" element={<Navigate to="../health" replace />} />
              </Route>

              <Route path="/account/settings" element={<AccountSettings />} />
              <Route path="/admin/firmware" element={<AdminFirmware />} />
              <Route path="/admin/organizations" element={<AdminOrgs />} />
              <Route path="/admin/devices" element={<AdminDevices />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ToastProvider>
          </OrgProvider>
          </AuthGate>
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
